import { useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'

export type RfqLineSpecification = components['schemas']['RfqLineSpecification']
export type ConfigurationEstimate = components['schemas']['ConfigurationEstimate']

/**
 * UID-346 (ADR-0117 D6a/D9) — the Bulk configure modal's live footer figure,
 * sourced from the REAL, RFQ-agnostic pricing seam `POST
 * /pricing/configuration-estimate`, per AC4. Deliberately NOT a client-side
 * computation — the three surfaces ADR-0117 found independently reinventing
 * one (`ConfigurePartsModal`'s old `null` stub, `RfqPreQuotePricingPreview`,
 * `PartsBulkConfigureModal`'s `estimateUnitPrice` average) are this seam's
 * reason to exist, and this hook is the first real caller.
 *
 * States mirror the design's three footer states (`dc.html:5814-5817`):
 *  - `not_ready` — material/finish not both chosen yet. PURELY CLIENT-SIDE;
 *    this hook fires no request at all in this state (ADR-0117 D9's explicit
 *    instruction).
 *  - `loading` — a request is in flight and no cached figure exists yet for
 *    this exact (specification, quantity). Rendered as plain "Estimating…"
 *    text, never a bare spinner (WCAG — text conveys state, not color/motion
 *    alone).
 *  - `estimated` / `manual_quote_required` — the server's `ConfigurationEstimate.outcome`,
 *    verbatim. Never derived/guessed client-side; the modal must reflect
 *    whatever the server actually decided (D8's manual-quote triggers are a
 *    server-owned rule).
 *  - `error` — the call failed (network, unexpected 4xx/5xx). No price is
 *    fabricated; the caller falls back to neutral copy.
 *
 * Debounced ~275ms, aborts a superseded in-flight request, and caches by an
 * exact-match key of `(specification, quantity, partSeed)` so repeatedly landing
 * on an already-seen combination (e.g. toggling a checkbox back off) never
 * re-hits the network — matches D9's "debounce ~250-300ms, abort superseded,
 * cache by (specification, quantity)", widened by UID-604 / ADR-0177 to include
 * the per-part seed (see {@link cacheKey}).
 */
export type ConfigurationEstimateState =
  | { readonly kind: 'not_ready' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'estimated'; readonly estimate: ConfigurationEstimate }
  | { readonly kind: 'manual_quote_required'; readonly estimate: ConfigurationEstimate }
  | { readonly kind: 'error' }

const DEBOUNCE_MS = 275

/**
 * Code review W3 (2026-08-27) — a CANONICAL fingerprint, total over every
 * field that feeds the server's price resolution (D6/D7/D8): `process`,
 * `material`, `finish`, `inspections`, `certificates`, `customInspection`,
 * and `quantity`. Previously `JSON.stringify([specification, quantity])`,
 * which fingerprints the OBJECT rather than the MEANING: `inspections`/
 * `certificates` are semantically SETS (their catalog membership, not their
 * selection order, is the only thing that matters), so two selections that
 * differ only in the ORDER a buyer happened to click them in — the same
 * result of `toggleListItem` appending to whichever end — produced different
 * JSON strings for what is the identical specification. That is a cache-
 * correctness bug on the money path: at best a spurious extra network round
 * trip; at worst, on the general principle that "the same input must always
 * resolve to the same cache entry", not a defense against it doing the
 * opposite (falsely COLLIDING two DIFFERENT specifications is impossible here
 * since every field participates in the tuple, but the fix is written to
 * hold that invariant explicitly rather than lean on `JSON.stringify`'s
 * incidental object-key-insertion-order behavior). Sorting the two array
 * fields makes selection ORDER irrelevant, matching the server's own
 * membership-only (not order-sensitive) validation.
 *
 * UID-604 / ADR-0177 — `partSeed` IS PART OF THIS KEY, and that is not
 * optional. The estimate stopped being a pure function of the specification the
 * moment price began depending on a per-part seed, so a key that fingerprints
 * only the specification means TWO DIFFERENT PARTS WITH THE SAME SPECIFICATION
 * SHARE ONE CACHE ENTRY — and the second part silently displays the first
 * part's price. That is a wrong price on a money surface, i.e. the same class of
 * defect UID-604 itself is, and it would ALSO be the failure this comment's own
 * "the same input must always resolve to the same cache entry" invariant is
 * written to forbid: with the seed omitted from the key, the key is no longer
 * total over the pricing inputs.
 */
function cacheKey(
  specification: RfqLineSpecification,
  quantity: number | undefined,
  partSeed: string | undefined,
): string {
  return JSON.stringify([
    specification.process,
    specification.material,
    specification.finish,
    [...(specification.inspections ?? [])].sort(),
    [...(specification.certificates ?? [])].sort(),
    specification.customInspection === true,
    quantity ?? null,
    // `null` for absent, so "no per-part identity" is its own entry rather than
    // colliding with some part that happens to be named the empty string.
    partSeed ?? null,
  ])
}

/**
 * `ready` gates whether a request is ever issued — callers pass their own
 * cascade's `materialChosen && finishChosen` (this hook has no opinion on
 * what "ready" means beyond "don't call me until then"). `specification`/
 * `quantity` should be STABLE VALUES (not re-created every render with a new
 * object identity) since they drive the effect's dependency array via a
 * cache-key comparison, not referential equality.
 *
 * `partSeed` (UID-604 / ADR-0177) MUST be the identity the SERVER snapshotted
 * for this part, resolved through `@misumi/shared`'s `configurationPartSeed`
 * over `RfqLineView.part.name` / `.partNumber`. It is the only way the live
 * footer figure and the figure the save path recomputes can agree — see ADR-0177
 * §4b. Do NOT pass a client-synthesized label (`RfqReviewLine.name`,
 * `displayNameFor`, `PartConfiguratorSubject.displayName`): `review-domain.ts`
 * invents `Item <first 8 chars of id>` when a sourceable has no name, a string
 * the server has never seen, which would guarantee DISAGREEMENT precisely on the
 * lines where identity is weakest. `undefined` — "no per-part identity
 * available" — is a legitimate value and prices at the un-seeded base; it is
 * what the bulk surface sends. Like its siblings it should be a STABLE VALUE
 * across renders.
 */
export function useConfigurationEstimate(
  ready: boolean,
  specification: RfqLineSpecification,
  quantity: number | undefined,
  partSeed: string | undefined,
): ConfigurationEstimateState {
  const [state, setState] = useState<ConfigurationEstimateState>({ kind: 'not_ready' })
  const cacheRef = useRef(new Map<string, ConfigurationEstimate>())
  // W4 — see the doc comment at its write site below. `null` means "no
  // in-flight request should still be able to write state" (not-ready, or no
  // request has been issued yet this render).
  const latestKeyRef = useRef<string | null>(null)
  const key = ready ? cacheKey(specification, quantity, partSeed) : null
  // `specification`/`quantity` are read via this ref inside the effect below,
  // rather than added to its dependency array — the effect must re-run only
  // when the LOGICAL input changes (captured exactly by `key`, which is
  // already a dependency), not on every render's new object identity for
  // `specification`. The ref is updated on every render, synchronously,
  // before the effect (whose own `[ready, key]` deps may not have changed)
  // could ever read it.
  const inputRef = useRef({ specification, quantity, partSeed })
  inputRef.current = { specification, quantity, partSeed }

  useEffect(() => {
    if (!ready || key === null) {
      // No key this run means no request should be able to land state either
      // — clears supersede tracking so a response from a PRIOR (now not-
      // ready) run can never match and write stale state.
      latestKeyRef.current = null
      setState({ kind: 'not_ready' })
      return
    }

    const cached = cacheRef.current.get(key)
    if (cached) {
      setState(
        cached.outcome === 'estimated'
          ? { kind: 'estimated', estimate: cached }
          : { kind: 'manual_quote_required', estimate: cached },
      )
      return
    }

    setState({ kind: 'loading' })
    const controller = new AbortController()
    // Code review W4 (2026-08-27) — supersede detection must not rely SOLELY
    // on the fetch layer actually rejecting with a named `AbortError`. That
    // is real but INDIRECT: it depends on `apiClient`'s transport honoring
    // `signal` and on the environment surfacing the abort as a same-named
    // rejection (a test double, a proxy, or a future transport swap could all
    // resolve normally instead) — and if a stale response resolves anyway,
    // the old code had NOTHING to stop it from calling `setState` and
    // silently overwriting a NEWER, more-correct estimate/price with a STALE
    // one for a specification the buyer has since changed away from. That is
    // the "wrong cached price" risk: not merely a UI flicker.
    //
    // `latestKeyRef` makes supersede detection UNAMBIGUOUS and independent of
    // the abort mechanism entirely: every response/rejection checks "is this
    // still the answer to the MOST RECENT question" against the ref (updated
    // synchronously, not via `setState`) before touching the cache or state.
    // The `AbortController` is kept too (still the right way to cancel the
    // underlying network request and avoid wasted bandwidth) but is no longer
    // the thing this hook trusts for correctness.
    latestKeyRef.current = key
    const timer = setTimeout(() => {
      const {
        specification: currentSpecification,
        quantity: currentQuantity,
        partSeed: currentPartSeed,
      } = inputRef.current
      void apiClient
        .POST('/pricing/configuration-estimate', {
          body: {
            specification: currentSpecification,
            ...(currentQuantity !== undefined ? { quantity: currentQuantity } : {}),
            // Spread in only when defined, the same pattern `quantity` uses:
            // "absent" is the honest wire spelling of "no per-part identity",
            // and the route normalizes absent/null/''/whitespace to one state.
            ...(currentPartSeed !== undefined ? { partSeed: currentPartSeed } : {}),
          },
          signal: controller.signal,
        })
        .then(({ data }) => {
          if (latestKeyRef.current !== key) return // superseded — a newer request owns state now.
          if (!data) {
            setState({ kind: 'error' })
            return
          }
          cacheRef.current.set(key, data)
          setState(
            data.outcome === 'estimated'
              ? { kind: 'estimated', estimate: data }
              : { kind: 'manual_quote_required', estimate: data },
          )
        })
        .catch((error: unknown) => {
          if (latestKeyRef.current !== key) return // superseded — ditto, regardless of error shape.
          // AbortError means a NEWER request superseded this one — kept as a
          // fast, allocation-free path for the common case (the check above
          // is the actual guarantee; this just avoids flashing 'error' first).
          if (error instanceof DOMException && error.name === 'AbortError') return
          setState({ kind: 'error' })
        })
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
    // `key` is the exact-match fingerprint of (specification, quantity,
    // partSeed) — the effect re-runs only when the LOGICAL input changes, not on
    // every render's new object identity for `specification`. `specification`/
    // `quantity`/`partSeed` are intentionally omitted from the dependency array:
    // they are read once per run inside the effect body via the ref, and `key`
    // already captures every bit of them that matters to WHEN this should
    // re-run.
  }, [ready, key])

  return state
}
